import React, { useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { tallyOpeningApi } from "../../data/tallyOpeningApi";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { DownloadIcon, UploadIcon } from "../../components/icons";
import { cn } from "../../components/cn";

/**
 * The bulk-upload block from the Zoho opening-balance screen, pointed at the
 * Tally sheet. Upload applies immediately: there is no separate review step.
 */
export function TallyOpeningImportPanel({
  clientId,
  disabled,
  onApplied,
}: {
  clientId: ClientId;
  disabled?: boolean;
  onApplied: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const locked = disabled || uploading;

  async function download() {
    setError(null);
    try {
      await tallyOpeningApi.template(clientId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not build that sheet.");
    }
  }

  async function upload(file: File) {
    setUploading(true);
    setError(null);
    setApplied(null);
    try {
      await tallyOpeningApi.upload(clientId, file);
      setApplied("The sheet was applied. The ledger balances below are up to date.");
      onApplied();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not read that sheet.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);
    if (locked) return;
    const file = event.dataTransfer.files[0];
    if (file) void upload(file);
  }

  return (
    <section className="flex flex-col gap-4 rounded-md border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-medium">Bulk Upload Ledger Balances</h4>
          <p className="text-sm text-muted-foreground">
            To update multiple ledger balances at once, download the ledger balance sheet below, enter the
            balances, and upload the completed sheet back. The uploaded values will update the ledger balances on
            this page.
          </p>
        </div>
        <CoButton onClick={() => void download()} disabled={locked}>
          <DownloadIcon className="size-4" />
          Download sheet
        </CoButton>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}
      {applied !== null && <Callout variant="info">{applied}</Callout>}

      <label
        onDragOver={(event) => {
          event.preventDefault();
          if (!locked) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed p-6 text-center transition-colors",
          dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40",
          locked && "pointer-events-none opacity-70"
        )}
      >
        <UploadIcon className="size-5 text-muted-foreground" />
        <span className="text-sm font-medium">
          {uploading ? "Reading the sheet…" : "Drop the filled-in sheet here, or click to choose"}
        </span>
        <span className="text-xs text-muted-foreground">.xlsx or .csv, up to 2 MB</span>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.csv"
          className="sr-only"
          disabled={locked}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
          }}
        />
      </label>
    </section>
  );
}
